import { PackagePaymentType, TrialPackageType } from '../types';

function getPriceWithSymbol(price: string | number, currency?: string, locale?: string) {
  const currencyValue = currency?.toUpperCase() || 'USD';
  const localeValue = locale || 'en-US';
  return new Intl.NumberFormat(localeValue, {
    style: 'currency',
    currency: currencyValue,
    currencyDisplay: "narrowSymbol"
  }).format(price as number);
}

export function getPackagePrices(packageItem: Record<string, any>, locale?: string) {
  const {
    packageType,
    trialPackageType,
    savePercent = 0,
    dailyPrice,
    dailyTrialPrice,
    weeklyPrice,
  } = packageItem || {};

  const { price, currency, trialPrice } = getSelectedPriceParams(packageItem);

  const priceValue = price;
  let trialPriceValue = '0.00';
  if (packageType === PackagePaymentType.SUBSCRIPTION && trialPackageType === TrialPackageType.STARTING_PRICE) {
    trialPriceValue = trialPrice;
  }

  function getPriceWithSymbolValue(priceVal: string | number) {
    const localeValue = locale || 'en-US';
    return getPriceWithSymbol(priceVal, currency, localeValue);
  }

  const pricesWithSymbol = {
    priceSym: getPriceWithSymbolValue(priceValue),
    trialPriceSym: getPriceWithSymbolValue(trialPriceValue),
    dailyPriceSym: dailyPrice ? getPriceWithSymbolValue(dailyPrice) : "",
    dailyTrialPriceSym: dailyTrialPrice ? getPriceWithSymbolValue(dailyTrialPrice) : "",
    weeklyPriceSym: weeklyPrice ? getPriceWithSymbolValue(weeklyPrice) : ""
  }

  return {
    price: `${priceValue} ${currency}`,
    priceBase: priceValue,
    currency,
    trialPrice: `${trialPriceValue} ${currency}`,
    trialPriceBase: trialPriceValue,
    savePercent,
    dailyPrice: dailyPrice ? `${dailyPrice} ${currency}` : "",
    dailyTrialPrice: dailyTrialPrice ? `${dailyTrialPrice} ${currency}` : "",
    weeklyPrice: weeklyPrice ? `${weeklyPrice} ${currency}` : "",
    ...pricesWithSymbol,
  };
}

export function getPackagePeriodsInfo(packageItem: Record<string, any>) {
  const {
    period = 0,
    periodType = "",
    packageType,
    trialPeriod = 0,
    trialPeriodType = "",
    trialPackageType
  } = packageItem || {};

  const periodObj = { period, periodType }
  const trialPeriodObj = { trialPeriod, trialPeriodType }

  if (packageType === PackagePaymentType.CONSUMABLE) {
    return {
      ...periodObj,
      ...trialPeriodObj
    }
  }

  if (trialPackageType === TrialPackageType.NO) {
    trialPeriodObj.trialPeriod = 0;
    trialPeriodObj.trialPeriodType = "";
  }

  if (periodType === "day" && period > 0 && period % 7 === 0) {
    periodObj.period = period / 7;
    periodObj.periodType = "week";
  }

  if (trialPeriodType === "day" && trialPeriod > 0 && trialPeriod % 7 === 0) {
    trialPeriodObj.trialPeriod = trialPeriod / 7;
    trialPeriodObj.trialPeriodType = "week";
  }

  return {
    ...periodObj,
    ...trialPeriodObj
  }
}

export function getPackagesPrices(packageItems: Record<string, any>[], locale?: string) {
  return packageItems.reduce((acc, curr) => {
    acc[curr.id] = getPackagePrices(curr, locale);
    return acc;
  }, {})
}

export function getTotalPayableAmount(packageItem: Record<string, any>, options?: { isTrialUsed?: boolean }) {
  if (!packageItem?.id) return '';
  const { isTrialUsed = false } = options || {};
  const {
    packageType,
    trialPackageType,
  } = packageItem;
  const { price, currency, trialPrice } = getSelectedPriceParams(packageItem);
  let priceValue = price;
  if (packageType === PackagePaymentType.SUBSCRIPTION && !isTrialUsed) {
    switch (trialPackageType) {
      case TrialPackageType.FREE_TRIAL: {
        priceValue = '0.00';
        break;
      }
      case TrialPackageType.STARTING_PRICE: {
        priceValue = trialPrice;
        break;
      }
      default:
        break;
    }
  }
  return `${priceValue} ${currency}`;
}

function getSelectedPriceParams(packageItem: Record<string, any>) {
  const {
    price,
    trialPrice,
    currency = 'USD',
    selectedPriceCurrency,
    selectedPrice = {}
  } = packageItem;
  const currencyValue = selectedPrice?.currency !== undefined ? selectedPrice?.currency : selectedPriceCurrency || currency;
  const trialPriceValue = selectedPrice?.trialPrice !== undefined ? selectedPrice?.trialPrice : trialPrice;
  const priceValue = selectedPrice?.price !== undefined ? selectedPrice?.price : price;

  return {
    price: priceValue,
    trialPrice: trialPriceValue,
    currency: currencyValue?.toUpperCase()
  };
}

export default getTotalPayableAmount;
